import { ReactElement, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useStore } from 'zens-store'
import {
  Color,
  Pagination,
  PaginationItem,
  PaginationProps,
  sortedRows,
  Table,
  TablePagination,
  TableProps,
  TableSort,
  Text,
} from 'zens-storybook'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  bottom: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  bottomText: {
    flex: 1,
    color: Color.metalGrey,
    fontSize: 14,
  },
})

interface Props extends TableProps, Omit<PaginationProps, 'value' | 'items' | 'onChange'> {
  pagination?: TablePagination
  paginationItems?: PaginationItem
  onPaginationChange?: (pagination: TablePagination) => void
  bottom?: string | ReactElement
}

const TablePaginated = (props: Props) => {
  const {
    style,
    rows,
    cols,
    onSortChange,
    defaultSort,
    dataIsSorted,
    count,
    pagination,
    renderCount,
    sizeLabel,
    onPaginationChange,
    contentContainerStyle,
    paginationItems,
    bottom,
    ...otherProps
  } = props
  const { i18n } = useStore()
  const colKeys = cols?.map((col) => col.key) || Object.keys(rows?.[0]?.data || {})

  const [sort, setSort] = useState<TableSort>(
    props.sort ||
      defaultSort || {
        field: colKeys?.[0] || '',
      },
  )
  const items = !sort?.field || dataIsSorted ? rows : sortedRows(rows, cols, sort, i18n.language)

  const pagedRows =
    pagination && items.length > pagination.size
      ? items.slice(pagination.page * pagination.size, (pagination.page + 1) * pagination.size)
      : items

  const updateSort = (sort: TableSort) => {
    onSortChange?.(sort)
    setSort(sort)
  }

  return (
    <View style={[styles.container, style]}>
      <Table
        locale={i18n.language}
        rows={pagedRows}
        cols={cols}
        sort={sort}
        onSortChange={updateSort}
        defaultSort={defaultSort}
        dataIsSorted
        {...otherProps}
      />
      {rows.length > 0 && (
        <View style={styles.bottom}>
          {typeof bottom === 'string' ? <Text style={styles.bottomText}>{bottom}</Text> : bottom}
          <Pagination
            count={count}
            value={pagination}
            renderCount={renderCount}
            sizeLabel={sizeLabel}
            maxSize={1000}
            onChange={onPaginationChange}
            contentContainerStyle={contentContainerStyle}
            items={paginationItems}
          />
        </View>
      )}
    </View>
  )
}

export default TablePaginated
